import React from 'react';
import styles from './apiServiceContext.module.scss';
import { isNullOrUndefined } from "../common/utility";
import { serviceWakeUp } from "../common/api";

import { useInterval } from "primereact/hooks";
import { ProgressBar } from "primereact/progressbar";

export const ApiServiceContext = React.createContext({
	serviceDetails: null,
	configuration: null,
	isApiConnecting: false
});

export const useApiServiceContextContext = () => React.useContext(ApiServiceContext);

/** A React context provider for initialising the API call stack
 * 1. The managed API only (type = **managed**)
 * 2. The managed API and downstream API (type = **full**)
 *
 * @param {*} props - ```{ serviceName = "", type = "full" | "managed", autoRefresh = true|false, refreshIntervalMs: 60000 }```
 * @returns
 */
export const ApiServiceContextProvider = (props) => {
	const [isWorking, setWorkingState] = React.useState(true);
	const [once, setOnce] = React.useState(false);
	const [serviceDetails, setServiceDetails] = React.useState(null);
	const [configuration, setConfiguration] = React.useState(null);
	const [refreshActive, setRefreshActive] = React.useState(!!props.autoRefresh);

	/** min 1 minute, max 5 minutes */
	const refreshIntervalMs = Math.min(Math.max(props?.refreshIntervalMs || 0, 60000), 300000);
	const {
		serviceName,
		type
	} = props;

	const acquireServiceDetails = async () => {
		console.debug(`ApiServiceContextProvider:acquireServiceDetails()`);

		setWorkingState(true);
		try {
			const responseData = await serviceWakeUp(type || "full");
			console.debug(`ApiServiceContextProvider:acquireServiceDetails() ${new Date().toISOString()}`, responseData);
			setServiceDetails(responseData?.serviceData || null);
			setConfiguration(responseData?.configuration || null);
		} catch (err) {
			console.error(`ApiServiceContextProvider:acquireServiceDetails() ➜ Error:`, err);
			setServiceDetails(null);
		} finally {
			setWorkingState(false);
		}
	};

	const doAcquireServiceDetails = React.useCallback(acquireServiceDetails, [type]);

	const onVisibilityChange = (ev) => {
		const isAcquireServiceDetailsAutoRefreshActive = document.visibilityState === "visible" && !!props.autoRefresh;
		console.debug(`ApiServiceContextProvider.onVisibilityChange()`, { isAcquireServiceDetailsAutoRefreshActive, visibilityState: document.visibilityState, ev });
		setRefreshActive(isAcquireServiceDetailsAutoRefreshActive);

		if (isAcquireServiceDetailsAutoRefreshActive) {
			// immediately refresh when tab becomes visible
			doAcquireServiceDetails();
		}
	};

	React.useEffect(() => {
		console.debug(`ApiServiceContextProvider.useEffect([]) -> ${once}`);
		if (once) {
			return;
		}

		console.debug(`ApiServiceContextProvider.useEffect([]) -> acquire service details...`);
		setOnce(true);
		doAcquireServiceDetails();

		console.debug(`ApiServiceContextProvider.useEffect([]) -> SETUP visibilitychange listener`);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			console.debug(`ApiServiceContextProvider.useEffect([]).return -> CLEANUP visibilitychange listener`);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, []);

	useInterval(
		doAcquireServiceDetails,
		refreshIntervalMs,
		refreshActive
	);

	const hasServiceDetails = !isNullOrUndefined(serviceDetails);
	return (
		<ApiServiceContext.Provider value={{
			serviceDetails,
			configuration,
			isApiConnecting: isWorking
		}}>
			<div className={styles.apiServiceContext}>
				<h3>
					{isWorking && (
						<div className={styles.progressBar}>
							<span>Connecting to Member Gateway......</span>
							<ProgressBar mode="indeterminate" style={{ height: '2px' }}></ProgressBar>
						</div>
					)}
					{!isWorking && (
						<div
							className={`${styles.statusIcon} ${hasServiceDetails ? styles.connected : styles.disconnected}`}
							title={`${hasServiceDetails ? `The ${serviceName ? `${serviceName} ` : ""}service is connected` : `The ${serviceName ? `${serviceName} ` : ""}service is not available`}`}
						>
							{!hasServiceDetails ? "Service not available" : ""}&nbsp;&nbsp;<i className="fas fa-globe"></i>
						</div>
					)}
				</h3>
				<div>{props.children}</div>
				<pre style={{ display: "none", fontSize: "12px", padding: "5px 10px" }}>{JSON.stringify(serviceDetails || {}, undefined, 2)}</pre>
			</div>
		</ApiServiceContext.Provider>
	);
};
